<div class="catalog-page pt-12!">

  <!-- ── Breadcrumb + Header (compacto) ──────────────────────────────────── -->
  <app-breadcrumb [items]="breadcrumbs()" />

  <header class="catalog-page__header">
    <h1 class="catalog-page__title">
      @if (isSearchMode()) {
        @if (searchQuery()) {
          Resultados para &ldquo;{{ searchQuery() }}&rdquo;
        } @else {
          Búsqueda
        }
      } @else {
        {{ categoryInfo()?.name ?? 'Tienda' }}
      }
    </h1>
    @if (!isSearchMode() && categoryInfo()?.description) {
      <p class="catalog-page__description">{{ categoryInfo()!.description }}</p>
    } @else if (isSearchMode() && searchQuery()) {
      <p class="catalog-page__description">
        {{ totalProducts() }}
        {{ totalProducts() === 1 ? 'producto encontrado' : 'productos encontrados' }}
      </p>
    }
  </header>

  <!-- ── Subcategories carousel (o categorías raíz si /tienda) ──────────── -->
  @if (displaySubcategories().length) {
    <app-subcategories-carousel [subcategories]="displaySubcategories()" />
  }

  <!-- ── Toolbar ────────────────────────────────────────────────────────────── -->
  <div class="catalog-page__toolbar">
    <span class="catalog-page__result-count">
      @if (loading()) {
        Cargando...
      } @else {
        {{ totalProducts() }} {{ totalProducts() === 1 ? 'producto' : 'productos' }}
      }
    </span>

    <app-search-bar
      class="catalog-page__toolbar-search"
      [placeholder]="searchPlaceholder()"
      (search)="onSearch($event)"
    />

    <div class="catalog-page__toolbar-actions">
      @if (showSidebar()) {
        <!-- Mobile filter toggle -->
        <button
          class="catalog-page__filter-toggle"
          type="button"
          (click)="filterDrawerOpen.set(true)"
        >
          <app-icon [icon]="filterIcon" [size]="16" />
          <span>Filtros</span>
        </button>
      }

      <app-page-size-select
        [value]="perPage()"
        (valueChange)="onPageSizeChange($event)"
      />

      <app-sort-dropdown
        [options]="sortOptions()"
        [value]="sortKey()"
        placeholder="Ordenar por"
        (valueChange)="onSortChange($event)"
      />
    </div>

    <!-- View mode toggle -->
    <div class="catalog-page__view-toggle" role="radiogroup" aria-label="Vista">
      <button
        class="catalog-page__view-btn"
        [class.catalog-page__view-btn--active]="viewMode() === 'grid'"
        type="button"
        aria-label="Vista cuadrícula"
        (click)="onViewModeChange('grid')"
      >
        <app-icon [icon]="gridIcon" [size]="18" />
      </button>
      <button
        class="catalog-page__view-btn"
        [class.catalog-page__view-btn--active]="viewMode() === 'list'"
        type="button"
        aria-label="Vista lista"
        (click)="onViewModeChange('list')"
      >
        <app-icon [icon]="listIcon" [size]="18" />
      </button>
    </div>
  </div>

  <!-- ── Active filters ─────────────────────────────────────────────────────── -->
  @if (hasActiveFilters()) {
    <app-active-filters-bar
      [activeFilters]="activeFilters()"
      [sections]="filterSections()"
      (removeFilter)="onRemoveFilter($event)"
      (clearAll)="onClearAllFilters()"
    />
  }

  <!-- ── Main content: sidebar + products ───────────────────────────────────── -->
  <div
    class="catalog-page__main"
    [class.catalog-page__main--no-sidebar]="!showSidebar()"
  >
    <!-- Sidebar -->
    @if (showSidebar()) {
      <app-filter-sidebar
        [sections]="filterSections()"
        [activeFilters]="activeFilters()"
        [isOpen]="filterDrawerOpen()"
        (filtersChange)="onFiltersChange($event)"
        (drawerClose)="filterDrawerOpen.set(false)"
      />
    }

    <!-- Product area -->
    <div class="catalog-page__content">
      @if (loading()) {
        <!-- Skeleton loading state -->
        <div
          class="catalog-page__grid"
          [class.catalog-page__grid--list]="viewMode() === 'list'"
        >
          @for (i of skeletonArray(); track i) {
            <app-product-card-skeleton [viewMode]="viewMode()" />
          }
        </div>
      } @else if (error()) {
        <!-- Error state -->
        <div class="catalog-page__empty">
          <p class="catalog-page__empty-title">Algo salió mal</p>
          <p class="catalog-page__empty-text">{{ error() }}</p>
          <app-button variant="primary" (click)="onRetry()">
            Reintentar
          </app-button>
        </div>
      } @else if (products().length === 0) {
        <!-- Empty state -->
        <div class="catalog-page__empty">
          @if (isSearchMode() && searchQuery()) {
            <img
              class="catalog-page__empty-mascot"
              src="/images/logo/empty_cart.png"
              alt=""
              width="140"
              height="140"
              loading="lazy"
              aria-hidden="true"
            />
            <p class="catalog-page__empty-title">
              No encontramos nada para &ldquo;{{ searchQuery() }}&rdquo;
            </p>
            <p class="catalog-page__empty-text">
              Probá con otro término, revisá la ortografía o explorá por categoría.
            </p>
            <a class="catalog-page__empty-link" routerLink="/tienda">
              Ir a la tienda
            </a>
          } @else if (hasActiveFilters() || searchQuery()) {
            <p class="catalog-page__empty-title">No encontramos productos</p>
            <p class="catalog-page__empty-text">
              Intenta ajustar los filtros o buscar algo diferente.
            </p>
            <app-button variant="secondary" (click)="onClearAllFilters()">
              Limpiar filtros
            </app-button>
          } @else {
            <p class="catalog-page__empty-title">Esta categoría aún no tiene productos</p>
            <p class="catalog-page__empty-text">
              Explora otras categorías o vuelve al inicio.
            </p>
            <a class="catalog-page__empty-link" routerLink="/">Ir al inicio</a>
          }
        </div>
      } @else {
        <!-- Product grid / list -->
        <div
          class="catalog-page__grid"
          [class.catalog-page__grid--list]="viewMode() === 'list'"
        >
          @for (product of products(); track product.id) {
            <app-product-card
              [product]="product"
              [viewMode]="viewMode()"
              (addToCart)="onAddToCart($event)"
              (reserveClick)="onReserve($event)"
              (zoomOpen)="onZoomOpen($event)"
              (notifyRequest)="onNotify($event)"
              (notifyCancel)="onNotifyCancel($event)"
              (cardClick)="onCardClick($event)"
            />
          }
        </div>

        <!-- Pagination -->
        @if (totalPages() > 1) {
          <div class="catalog-page__pagination">
            <app-pagination
              [currentPage]="currentPage()"
              [totalPages]="totalPages()"
              (pageChange)="onPageChange($event)"
            />
          </div>
        }
      }

      <!-- ── Blog articles carousel ──────────────────────────────────────── -->
      <!-- Oculto hasta el lanzamiento del blog en fases posteriores. -->
      <!-- @if (relatedArticles().length) {
        <app-blog-carousel [articles]="relatedArticles()" [autoplay]="true" />
      } -->

    </div>
  </div>
</div>

<!-- ── Zoom modal ──────────────────────────────────────────────────────────── -->
@if (zoomProduct(); as product) {
  <app-product-zoom-modal
    [product]="product"
    [notifyActive]="zoomNotifyActive()"
    (close)="onZoomClose()"
    (addToCart)="onZoomAddToCart($event)"
    (reserveRequest)="onZoomReserve($event)"
    (notifyRequest)="onNotify($event)"
    (notifyCancel)="onNotifyCancel($event)"
    (viewDetails)="onViewDetails($event)"
  />
}
